import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HeaderContainer } from '../../../common/sharedStyledComponents/GenericStyles.styled';
import { AppHeader } from '../../../common/sharedComponents/AppHeader';
import { useTranslation } from 'react-i18next';
import {
  BoostersContainer,
  MyBoosterHeader,
  MyBoosterRocketIcon,
  MyBoosterHeaderSubtitle,
  MyBoosterContent,
  CardContent,
  CardTitle,
  MyBoosterHeaderContent,
  BoosterCardWrapper,
  IconWrapper,
  ErrorMessage,
  EmptyMessage,
} from '../Boosters.styled';
import { RocketBoosters } from '../../../common/icons/RocketBoosters';
import { Body4, Headline2, Headline4 } from '../../../common/typography';
import { Card } from '../../../common/sharedComponents/Card';
import { CircleCheckbox } from '../../../common/icons/CircleCheckbox';
import { CircleCheckedbox } from '../../../common/icons/CircleCheckedbox';
import { ChevronRight } from '../../../common/icons/ChevronRight';
import { fetchWeeklyBoosters } from '../../../../requests/learning';
import {
  IBoostersResponse,
  IWeeklyBoosterWithViewStatusDto,
  AllowedDepartment,
} from '../../../@types/boosters';
import { MyBoostersSkeleton } from './MyBoostersSkeleton';
import RichText from '../../../common/sharedComponents/RichText';
import { TopicsMap } from '../../../@types/topics';
import {
  boostersResponseToTopicsMap,
  getFirstTopicName,
} from './parseTopicsMap';

interface IMyBoosterState {
  department: AllowedDepartment;
}

export function MyBooster () {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [boostersData, setBoostersData] = useState<IBoostersResponse | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const { state } = useLocation() as { state?: IMyBoosterState };
  const department = state && state.department;

  useEffect(() => {
    const fetchBoosters = async () => {
      if (!department) {
        setError(t('COMMON.LEARNING.BOOSTERS.ERRORS.DEPARTMENT_REQUIRED'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await fetchWeeklyBoosters(department);
        setBoostersData(data);
      } catch (err: any) {
        setError((err && err.message) || t('COMMON.LEARNING.BOOSTERS.ERRORS.FAILED_TO_FETCH'));
      } finally {
        setLoading(false);
      }
    };

    fetchBoosters();
  }, []);

  // Parse boosters data into TopicsMap format for better response handling
  const topicsMap: TopicsMap = useMemo(() => {
    return boostersResponseToTopicsMap(boostersData);
  }, [boostersData]);

  // Get topic title from the parsed TopicsMap
  const topicTitle = useMemo(() => {
    return getFirstTopicName(topicsMap);
  }, [topicsMap]);

  // Map boosters to days format (keeping original structure for navigation)
  const myBoosterDays =
    (boostersData &&
      boostersData.boosters &&
      boostersData.boosters.map(
        (booster: IWeeklyBoosterWithViewStatusDto, index: number) => ({
          id: booster.id,
          checked: booster.isViewed,
          tagline:
            (booster.content && booster.content.headerLine) ||
            `${t('COMMON.LEARNING.BOOSTERS.LABELS.BOOSTER')} ${index + 1}`,
          booster,
        })
      )) ||
    [];

  // Map department enum to translation key
  const getDepartmentTranslationKey = (
    myBoostedDepartment: AllowedDepartment
  ): string => {
    const departmentMap: { [key in AllowedDepartment]: string } = {
      [AllowedDepartment.FRONT_OF_HOUSE]:
        'COMMON.LEARNING.TITLES.FRONT_OF_HOUSE',
      [AllowedDepartment.FOOD_BEVERAGE]:
        'COMMON.LEARNING.TITLES.FOOD_AND_BEVERAGE',
      [AllowedDepartment.HOUSEKEEPING]: 'COMMON.LEARNING.TITLES.HOUSEKEEPING',
      [AllowedDepartment.PRODUCT]: 'COMMON.LEARNING.TITLES.PRODUCT',
    };
    return departmentMap[myBoostedDepartment];
  };

  // Generate day label string
  const getDayLabel = (dayIndex: number, tagline: string): string =>
    `${t('COMMON.LEARNING.BOOSTERS.LABELS.DAY')} ${dayIndex + 1} - ${tagline}`;

  const departmentTitle = department
    ? t(getDepartmentTranslationKey(department))
    : '';
  const headerTitle = departmentTitle
    ? departmentTitle
    : t('COMMON.LEARNING.TITLES.BOOSTERS');

  return (
    <>
      <HeaderContainer>
        <AppHeader title={headerTitle} navigateTo="/learning/boosters" />
      </HeaderContainer>
      <BoostersContainer>
        <MyBoosterHeader>
          <MyBoosterHeaderContent>
            <Headline2 color="oxfordBlue">
              {t('COMMON.LEARNING.TITLES.MY_BOOSTERS')}
            </Headline2>
            <MyBoosterHeaderSubtitle>
              <Headline4 color="oxfordBlue">
                {t('COMMON.LEARNING.SUBTITLES.TOPIC_OF_THE_WEEK')}{' '}
              </Headline4>
              <Headline4 color="oxfordBlue">{topicTitle} </Headline4>
            </MyBoosterHeaderSubtitle>
          </MyBoosterHeaderContent>
          <IconWrapper>
            <MyBoosterRocketIcon>
              <RocketBoosters />
            </MyBoosterRocketIcon>
          </IconWrapper>
        </MyBoosterHeader>
        <MyBoosterContent>
          {loading && <MyBoostersSkeleton />}
          {error && <ErrorMessage>{t('COMMON.LEARNING.BOOSTERS.LABELS.ERROR')} {error}</ErrorMessage>}
          {!loading && !error && myBoosterDays.length === 0 && (
            <EmptyMessage>{t('COMMON.LEARNING.BOOSTERS.LABELS.NO_BOOSTERS_AVAILABLE')}</EmptyMessage>
          )}
          {!loading &&
            !error &&
            myBoosterDays.map((day, index) => (
              <BoosterCardWrapper key={day.id}>
                <Card
                  onClick={() => {
                    navigate('/learning/dailyBooster', {
                      state: {
                        ...day,
                        department: state && state.department,
                        dayNumber: index + 1,
                        viewedCountInCycle: boostersData && boostersData.viewedCountInCycle ? boostersData.viewedCountInCycle : 0,
                        totalBoostersInCycle: boostersData && boostersData.totalBoostersInCycle ? boostersData.totalBoostersInCycle : 0,
                      },  
                    });
                  }}
                >
                  <CardContent>
                    <CardTitle>
                      {day.checked ? <CircleCheckedbox /> : <CircleCheckbox />}
                      <RichText content={getDayLabel(index, day.tagline)} stripTags={true} bold={day.checked} />
                    </CardTitle>
                    <IconWrapper>
                      <ChevronRight />
                    </IconWrapper>
                  </CardContent>
                </Card>
              </BoosterCardWrapper>
            ))}
        </MyBoosterContent>
      </BoostersContainer>
    </>
  );
}
